import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useKidsChurchMessages } from '../../hooks/useKidsChurchMessages';
import { supabaseChatService } from '../../services/supabaseChatService';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabase';

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: var(--surfaceVariant);
  display: flex;
  flex-direction: column;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--outline);
  background: var(--surface);
`;

const HeaderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--outline);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--onSurfaceVariant);
  font-weight: 700;
`;

const HeaderAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderName = styled.div`
  font-weight: 600;
  color: var(--onSurface);
`;

const Bubble = styled.div<{ me: boolean }>`
  max-width: 60%;
  margin-bottom: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 18px;
  background: ${({ me }) => (me ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ me }) => (me ? 'var(--onPrimary)' : 'var(--onSurface)')};
  align-self: ${({ me }) => (me ? 'flex-end' : 'flex-start')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  position: relative;

  &:hover span {
    opacity: 1;
  }
`;

const MessageImageLink = styled.a`
  display: block;
`;

const MessageImage = styled.img`
  display: block;
  max-width: min(320px, 100%);
  max-height: 320px;
  border-radius: 12px;
  object-fit: cover;
  margin-bottom: 0.5rem;
`;

const Timestamp = styled.span`
  position: absolute;
  bottom: -16px;
  right: 4px;
  font-size: 10px;
  color: var(--onSurfaceVariant);
  opacity: 0;
  transition: opacity 0.2s;
`;

const Composer = styled.form`
  padding: 0.75rem;
  border-top: 1px solid var(--outline);
  display: flex;
`;

const Input = styled.textarea`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 6px;
  resize: none;
  min-height: 42px;
`;

const SendButton = styled.button`
  margin-left: 0.5rem;
  background: var(--primary);
  color: white;
  border: none;
  padding: 0 1rem;
  border-radius: 6px;
`;

interface Props {
  otherUserId: string | null;
}

export const ChatWindow: React.FC<Props> = ({ otherUserId }) => {
  const { user } = useAuth();
  const currentId = user?.id || '';
  const queryClient = useQueryClient();
  const { data: messages } = useKidsChurchMessages(currentId, otherUserId || '');
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState('');
  const [otherUser, setOtherUser] = useState<{
    id: string;
    full_name: string | null;
    avatar: string | null;
  } | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!currentId || !otherUserId) return;
    const hasUnread = messages.some(
      (message) =>
        message.receiver_id === currentId &&
        message.sender_id === otherUserId &&
        !message.read_at
    );
    if (!hasUnread) return;

    supabaseChatService
      .markMessagesAsRead(currentId, otherUserId)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['kids-chat-users', currentId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ['kids-chat-messages', currentId, otherUserId],
        });
      })
      .catch(() => undefined);
  }, [currentId, otherUserId, messages, queryClient]);

  useEffect(() => {
    if (!otherUserId) {
      setOtherUser(null);
      return;
    }
    const loadRecipient = async () => {
      try {
        const { data } = await supabase
          .from('users')
          .select('id, full_name, avatar')
          .eq('id', otherUserId)
          .maybeSingle();
        if (data) {
          setOtherUser({
            id: data.id,
            full_name: data.full_name ?? null,
            avatar: data.avatar ?? null,
          });
        } else {
          setOtherUser(null);
        }
      } catch {
        setOtherUser(null);
      }
    };
    loadRecipient();
  }, [otherUserId]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || !otherUserId) return;
    await supabaseChatService.sendMessage(currentId, otherUserId, text.trim());
    setText('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
    const el = event.target;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  if (!otherUserId) {
    return <div style={{ padding: '1rem' }}>Select a conversation</div>;
  }

  return (
    <Container>
      <HeaderRow>
        <HeaderAvatar>
          {otherUser?.avatar ? (
            <HeaderAvatarImage src={otherUser.avatar} alt={otherUser.full_name ?? 'User avatar'} />
          ) : (
            (otherUser?.full_name || 'U').charAt(0).toUpperCase()
          )}
        </HeaderAvatar>
        <HeaderName>{otherUser?.full_name ?? 'User'}</HeaderName>
      </HeaderRow>
      <MessagesContainer>
        {messages.map((m) => {
          const imageUrl = (m.image_url ?? '').trim();
          const textContent = (m.content ?? '').trim();
          return (
            <Bubble key={m.id} me={m.sender_id === currentId}>
              {imageUrl ? (
                <MessageImageLink href={imageUrl} target="_blank" rel="noopener noreferrer">
                  <MessageImage src={imageUrl} alt="Message attachment" />
                </MessageImageLink>
              ) : null}
              {textContent}
              <Timestamp>
                {new Date(m.created_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Timestamp>
            </Bubble>
          );
        })}
        <div ref={bottomRef} />
      </MessagesContainer>
      <Composer onSubmit={handleSend}>
        <Input
          ref={inputRef}
          value={text}
          onChange={handleInputChange}
          placeholder="Type a message"
          rows={2}
        />
        <SendButton type="submit">Send</SendButton>
      </Composer>
    </Container>
  );
};
